// src/hooks/useEntityImage.ts
//
// Resolve an EntityImageRef ({category, identifier}) to a thumbnail
// URL with caption + attribution. Backed by Wikipedia's REST API
// summary endpoint, which is CORS-friendly, key-free, and has good
// coverage for historical figures, paintings, monuments, events,
// architecture styles, etc.
//
// Endpoint:
//   https://en.wikipedia.org/api/rest_v1/page/summary/<title>
//
// Response shape (relevant fields):
//   {
//     title: "Albrecht Dürer",
//     description: "German painter and theorist (1471–1528)",
//     extract: "Albrecht Dürer was a German Renaissance...",
//     thumbnail: { source: "...thumb.jpg", width, height },
//     originalimage: { source: "...full.jpg", width, height },
//     content_urls: { desktop: { page: "https://en.wikipedia.org/wiki/Albrecht_D%C3%BCrer" } }
//   }
//
// Wikipedia thumbnails are appropriate for inline display (300-400px
// max). originalimage is HD; we use thumbnail to keep mobile data
// usage modest, but capture both URLs in case a lightbox wants the
// full version.
//
// Caching: in-memory cache keyed on `<category>:<identifier>` so we
// don't refetch on remount or re-render. Wikipedia's cache headers
// already give browsers some help, but explicit memoization avoids
// even the conditional GETs.
//
// Failure modes:
//   - 404 (no Wikipedia article)        → null
//   - Article exists but no image       → null
//   - Network failure / CORS / parse    → null
// Callers should render nothing on null — no broken-image
// placeholders.

import { useEffect, useState } from 'react';

export interface EntityImage {
  /** Mid-resolution URL good for inline (300-500px wide). */
  thumbnailUrl: string;
  /** Full-resolution URL for lightbox / hi-DPI. */
  fullUrl: string;
  /** Wikipedia article title (resolved — may differ slightly from
   *  the input identifier due to redirects). */
  title: string;
  /** Wikipedia's one-line description, e.g. "German Renaissance
   *  painter (1471–1528)". May be undefined for some articles. */
  description?: string;
  /** Public Wikipedia URL for the article — used for "learn more"
   *  links and required attribution. */
  articleUrl: string;
}

// Module-scope cache. Persists for the page lifetime; a successful
// lookup (or null result) is reused across all components that ask
// for the same key.
const cache = new Map<string, EntityImage | null>();
// In-flight promises to dedupe concurrent fetches for the same key.
const inflight = new Map<string, Promise<EntityImage | null>>();

const cacheKey = (category: string, identifier: string): string =>
  `${category.toLowerCase()}:${identifier.toLowerCase()}`;

async function fetchWikipediaSummary(
  identifier: string,
): Promise<EntityImage | null> {
  // Wikipedia title encoding: spaces → underscores per the URL
  // convention, then URL-encode for safety. Wikipedia also accepts
  // spaces directly but underscores are more reliable across edge
  // cases (titles with non-ASCII characters, etc.).
  const title = identifier.replace(/\s+/g, '_');
  const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;

  try {
    const resp = await fetch(url, {
      headers: {
        // Wikipedia's REST API recommends a custom User-Agent for
        // identifiable traffic, but browser fetch() can't set
        // User-Agent. The Api-User-Agent custom header is the
        // documented workaround, though Wikipedia accepts requests
        // either way.
        'Api-User-Agent': 'instant.tours (https://instant.tours)',
      },
    });
    if (!resp.ok) {
      return null;
    }
    const json = (await resp.json()) as {
      title?: string;
      description?: string;
      thumbnail?: { source?: string };
      originalimage?: { source?: string };
      content_urls?: { desktop?: { page?: string } };
      type?: string;
    };
    // Disambiguation pages have type='disambiguation' and aren't
    // useful for inline images. Skip them — caller falls back to
    // null.
    if (json.type === 'disambiguation') return null;
    const thumb = json.thumbnail?.source;
    const full = json.originalimage?.source;
    // Need at least the thumbnail to render anything inline.
    if (!thumb) return null;
    return {
      thumbnailUrl: thumb,
      fullUrl: full ?? thumb,
      title: json.title ?? identifier,
      description: json.description,
      articleUrl:
        json.content_urls?.desktop?.page ??
        `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`,
    };
  } catch {
    return null;
  }
}

/**
 * Hook to resolve an EntityImageRef to a thumbnail. Returns null
 * while loading and on failure — render nothing in either case.
 */
export function useEntityImage(
  category: string,
  identifier: string,
): EntityImage | null {
  const [data, setData] = useState<EntityImage | null>(() => {
    // Sync hit on cached entries. Avoids the loading flicker for
    // entities the user has already seen earlier in the chat.
    const key = cacheKey(category, identifier);
    return cache.has(key) ? (cache.get(key) ?? null) : null;
  });

  useEffect(() => {
    if (!category || !identifier) return;
    const key = cacheKey(category, identifier);

    if (cache.has(key)) {
      setData(cache.get(key) ?? null);
      return;
    }

    let cancelled = false;

    // Dedupe concurrent fetches for the same entity.
    let promise = inflight.get(key);
    if (!promise) {
      promise = fetchWikipediaSummary(identifier).then((result) => {
        cache.set(key, result);
        inflight.delete(key);
        return result;
      });
      inflight.set(key, promise);
    }

    promise.then((result) => {
      if (cancelled) return;
      setData(result);
    });

    return () => {
      cancelled = true;
    };
  }, [category, identifier]);

  return data;
}
